import { getArea } from 'ol/sphere';
import { createEditingStyle } from 'ol/style/Style';

import type { EventKey } from 'cadenza/utils/event-util';
import { on, unByKey } from 'cadenza/utils/event-util';
import { formatArea } from 'cadenza/utils/format/format';
import type { Options } from 'cadenza/map/interactions/measure';
import { attachMeasureOverlayToFeature, Measure } from 'cadenza/map/interactions/measure';
import { TooltipOverlay } from 'cadenza/map/tooltip/tooltip-overlay';
import { GeometryType } from 'cadenza/map/geom/geometry-type';
import { MapProperty } from 'cadenza/map/map-property';
import { assertNonNullable } from 'cadenza/utils/custom-error';

import type { Polygon } from 'ol/geom';
import type { StyleFunction } from 'ol/style/Style';
import type { Map, Feature } from 'ol';
import type { DrawEvent } from 'ol/interaction/Draw';

export class MeasureArea extends Measure {

  #toggleTooltipEventKey?: EventKey;

  constructor (map: Map, options?: Options) {
    super(map, 'Polygon', createMeasureAreaStyle(map), options);
  }

  override onMeasureStart (event: DrawEvent) {
    super.onMeasureStart(event);
    this.#toggleTooltipEventKey = on(
      this.measureTooltip!.element!,
      'mouseenter',
      () => this.#toggleMeasureTooltipPositioning()
    );
  }

  override onMeasureEnd (event: DrawEvent) {
    super.onMeasureEnd(event);
    unByKey(this.#toggleTooltipEventKey);
  }

  override onMeasureAbort () {
    super.onMeasureAbort();
    unByKey(this.#toggleTooltipEventKey);
  }

  protected updateMeasure (measure: TooltipOverlay, geometry: Polygon): void {
    const projection = this.map.getView().getProjection();
    const hasArea = geometry.getCoordinates()[0].length > 3;
    const measurement = formatArea(hasArea ? getArea(geometry, { projection }) : 0);
    const measurePosition = geometry.getInteriorPoint().getCoordinates();
    measure.message = measurement;
    measure.position = measurePosition;
  }

  /**
   * Toggles the tooltip positioning. For example to avoid interfering it with the mouse cursor.
   * The positioning alternates between current positioning and the opposite (top-right becomes bottom-left) for example.
   * The arrow styling and offset are also adapted accordingly.
   */
  #toggleMeasureTooltipPositioning () {
    if (this.measureTooltip!.positioning === 'bottom-center') {
      this.measureTooltip!.positioning = 'top-center';
    } else {
      this.measureTooltip!.positioning = 'bottom-center';
    }
  }

  get resettable () {
    return true;
  }

}

export function createMeasureAreaStyle (map: Map) {
  const cursorStyle = createEditingStyle()[GeometryType.POINT][0];
  const polygonStyle = map.get(MapProperty.DRAW_STYLES)['Polygon'];
  return ((feature: Feature) => {
    const geometry = feature.getGeometry();
    if (!geometry) {
      return;
    }
    if (geometry.getType() === 'Point') {
      return cursorStyle;
    }
    return polygonStyle;
  }) as StyleFunction;
}

export function setupAreaMeasureOnFeature (feature: Feature<Polygon>, map: Map, unit: 'metric' | 'imperial') {
  const measure = feature.getGeometry();
  assertNonNullable(measure);
  const projection = map.getView().getProjection();
  const hasArea = measure.getCoordinates()[0].length > 3;
  const measureText = formatArea(hasArea ? getArea(measure, { projection }) : 0, unit === 'metric');
  const measurePosition = measure.getInteriorPoint().getCoordinates();
  const overlay = new TooltipOverlay(map, {
    followMouse: false,
    showArrow: true,
    positioning: 'top-center',
    coloring: 'primary',
    message: measureText,
    position: measurePosition
  });
  overlay.activate();
  overlay.show();
  attachMeasureOverlayToFeature(feature, overlay);
}
